import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryToggleOffOutlinedIcon from "@mui/icons-material/HistoryToggleOffOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, type SyntheticEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ChangeSourceBadge } from "../../components/UIToolkit/Badges/ChangeSourceBadge";
import { MetaChip } from "../../components/UIToolkit/Badges/MetaChip";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { DateRangeSelector } from "../../components/UIToolkit/molecules/DateRangeSelector";
import { EmptyState } from "../../components/UIToolkit/molecules/EmptyState";
import { StatStrip, type StatStripItem } from "../../components/UIToolkit/molecules/StatStrip";
import { SystemDataGrid } from "../../components/UIToolkit/Tables/SystemDataGrid";
import type { SystemGridColDef } from "../../components/UIToolkit/Tables/SystemDataGrid/types";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useRosterLookup } from "../../hooks/useRosterLookup";
import { FONT_SIZE, INK, NEUTRAL } from "../../theme/tokens";
import { CHANGELOG_PARAM, readShiftChangelogFocus } from "../../utils/shiftChangelogLink";
import { FocusedShiftLineage } from "./FocusedShiftLineage";
import { EXPAND_ROW, expandHeight, ShiftSyncTimeline } from "./ShiftSyncTimeline";
import { reasonLabel } from "./shiftLineage";
import { OUTCOME_HINT_KEY, OUTCOME_LABEL_KEY, OUTCOME_TONE, STEP_LABEL_KEY, STEP_TONE } from "./shiftLineageDisplay";
import { groupEntriesByShift, type ShiftLineageGroup, summarizeShiftGroups } from "./shiftLineageGroups";
import { useNurseShiftLineageQuery } from "./useNurseShiftLineageQuery";
import { useRawSyncFile } from "./useRawSyncFile";

type Row = ShiftLineageGroup;

/** Last path segment of an S3 key, for a compact one-line label (full key stays in the tooltip + expander). */
const fileBasename = (key: string): string => key.split("/").pop() || key;

export const ShiftLineageView: FC = () => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { timezone } = useDepartmentFlags();
	const { roster, getNurseFormattedName, isError: isRosterError } = useRosterLookup(chosenDepartment.department_id);
	const { track } = useAnalytics();
	const { isSupport, openRawFile, copyFileKey } = useRawSyncFile();

	const [start, setStart] = useState<DateTime>(DateTime.now().minus({ day: 30 }).startOf("day"));
	const [end, setEnd] = useState<DateTime>(DateTime.now().startOf("day"));

	// A link from a shift's raw punches (day view / profile) arrives with the employee and the shift in
	// the URL. The shift id turns this view into a single-shift reading of the same data; clearing it
	// drops back to the employee's full log with the filter already set.
	const [searchParams, setSearchParams] = useSearchParams();
	const focus = useMemo(() => readShiftChangelogFocus(searchParams), [searchParams]);
	const [nurseId, setNurseId] = useState<number | undefined>(focus.nurseId);

	// Follow a NEW deep link that lands while this view is already mounted (support users jump between
	// shifts). Only ever sets a nurse the link named — it must not clear a filter the user chose.
	useEffect(() => {
		if (focus.nurseId !== undefined) setNurseId(focus.nurseId);
	}, [focus.nurseId]);

	useEffect(() => {
		if (focus.shiftId) track("shift_change_log_focus_opened", { department_id: chosenDepartment?.department_id, shift_id: focus.shiftId });
	}, [focus.shiftId, chosenDepartment?.department_id, track]);

	/** Leave focus mode: drop the shift from the URL and keep the employee filter. */
	const clearFocus = () => {
		const next = new URLSearchParams(searchParams);
		next.delete(CHANGELOG_PARAM.shiftId);
		setSearchParams(next, { replace: true });
		track("shift_change_log_focus_cleared", { department_id: chosenDepartment?.department_id });
	};

	// Not fetched while focused: the focus panel reads the shift directly, so the employee's 30-day list
	// would be a request whose result is never rendered.
	const { data: entries = [], isLoading, isError } = useNurseShiftLineageQuery({ start, end, nurseId: focus.shiftId ? undefined : nurseId });

	// One row per shift instead of one per raw decision — a fortnight of re-syncs is otherwise dozens of
	// near-identical rows the reader has to join by eye.
	const groups = useMemo(() => groupEntriesByShift(entries), [entries]);
	const summary = useMemo(() => summarizeShiftGroups(groups), [groups]);

	const nurseOptions = useMemo<SystemAutoCompleteOption<number>[]>(
		// DEFAULT: no options until the roster loads; the picker is disabled meanwhile.
		() => (roster ?? []).map((nurse) => ({ label: getNurseFormattedName(nurse.id), value: nurse.id })).sort((a, b) => a.label.localeCompare(b.label)),
		[roster, getNurseFormattedName],
	);
	// DEFAULT: no picked option while the roster loads, or for a linked employee it lacks — the filter still applies.
	const pickedNurseOption = nurseOptions.find((option) => option.value === nurseId) ?? null;
	const focusedEmployeeName = nurseOptions.find((option) => option.value === focus.nurseId)?.label;

	const formatWhen = (value: DateTime | null): string => value?.setZone(timezone).toFormat("MMM d, HH:mm") ?? "—";

	/** The shift a row is about: its date and clock window, in the department timezone. */
	const shiftSubject = (row: Row): { date: string; window: string } | null => {
		const startAt = row.startAt?.setZone(timezone);
		if (!startAt) return null;
		const endAt = row.endAt?.setZone(timezone);
		return { date: startAt.toFormat("EEE, MMM d"), window: endAt ? `${startAt.toFormat("HH:mm")}–${endAt.toFormat("HH:mm")}` : startAt.toFormat("HH:mm") };
	};

	const outcomeLabel = (row: Row): string => t(OUTCOME_LABEL_KEY[row.outcome]);

	/** Why the shift ended up in this state — the reason of the decision that produced it. */
	const latestReason = (row: Row): string => reasonLabel(t, row.latestStep.primaryReason);

	const summaryItems: StatStripItem[] = [
		{ label: t("employeeChangelog.shiftLineage.summary.shifts"), value: summary.shifts, dataTestId: "shift-lineage-summary-shifts" },
		{ label: t("employeeChangelog.shiftLineage.summary.active"), value: summary.active, dataTestId: "shift-lineage-summary-active" },
		{ label: t("employeeChangelog.shiftLineage.summary.removed"), value: summary.removed, dataTestId: "shift-lineage-summary-removed" },
		{ label: t("employeeChangelog.shiftLineage.summary.notCreated"), value: summary.notCreated, dataTestId: "shift-lineage-summary-not-created" },
		{ label: t("employeeChangelog.shiftLineage.summary.syncRuns"), value: summary.syncRuns, dataTestId: "shift-lineage-summary-runs" },
		{ label: t("employeeChangelog.shiftLineage.summary.decisions"), value: summary.changes, dataTestId: "shift-lineage-summary-decisions" },
	];

	// Not memoized: the cell renderers close over `formatWhen`/`shiftSubject` (recreated each render), so a
	// useMemo here would either go stale or need those as deps and re-run every render anyway.
	//
	// Each `field` is a real key of the row because `searchableColumns` requires one; every column renders
	// through `renderCell` and is `sortable: false`, so the raw key value is never read as a cell value.
	const columns: SystemGridColDef<Row>[] = [
		{
			field: "startAt",
			headerName: t("employeeChangelog.shiftLineage.column.shift"),
			flex: 1.1,
			minWidth: 165,
			sortable: false,
			renderCell: (params) => {
				const subject = shiftSubject(params.row);
				if (!subject)
					return <Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.DISABLED }}>{t("employeeChangelog.shiftLineage.shift.noWindow")}</Typography>;
				return (
					<Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, fontWeight: 600, whiteSpace: "nowrap" }}>{subject.date}</Typography>
						<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, color: INK.MUTED, whiteSpace: "nowrap" }}>{subject.window}</Typography>
					</Box>
				);
			},
		},
		{
			field: "outcome",
			headerName: t("employeeChangelog.shiftLineage.column.outcome"),
			flex: 0.85,
			minWidth: 150,
			sortable: false,
			renderCell: (params) => (
				<MetaChip
					label={outcomeLabel(params.row)}
					tone={OUTCOME_TONE[params.row.outcome]}
					tooltip={t(OUTCOME_HINT_KEY[params.row.outcome])}
					dataTestId={`shift-lineage-outcome-${params.row.key}`}
				/>
			),
		},
		{
			field: "latestStep",
			headerName: t("employeeChangelog.shiftLineage.column.syncActivity"),
			// The widest column: it carries the chip, the full reason sentence and the churn chip, and the
			// reason is the part that must not truncate — it's the explanation the reader came for.
			flex: 2.3,
			minWidth: 330,
			sortable: false,
			renderCell: (params) => {
				const { steps, latestStep } = params.row;
				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
						<MetaChip
							label={t(STEP_LABEL_KEY[latestStep.status])}
							tone={STEP_TONE[latestStep.status]}
							dataTestId={`shift-lineage-step-${params.row.key}`}
							sx={{ flexShrink: 0 }}
						/>
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							{latestReason(params.row)}
						</Typography>
						{steps.length > 1 && (
							// The churn IS the finding on a re-synced shift: one glance says "this was rewritten N
							// times", which the flat feed could only convey as N separate rows.
							<MetaChip
								label={t("employeeChangelog.shiftLineage.churn.syncs", { count: steps.length })}
								tooltip={t("employeeChangelog.shiftLineage.churn.tooltip", { count: params.row.changeCount })}
								dataTestId={`shift-lineage-churn-${params.row.key}`}
								sx={{ flexShrink: 0 }}
							/>
						)}
					</Box>
				);
			},
		},
		{
			field: "steps",
			headerName: t("employeeChangelog.shiftLineage.column.lastSynced"),
			flex: 0.8,
			minWidth: 140,
			sortable: false,
			renderCell: (params) => (
				<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY, whiteSpace: "nowrap" }}>{formatWhen(params.row.latestStep.at)}</Typography>
			),
		},
		{
			field: "key",
			headerName: t("employeeChangelog.shiftLineage.column.source"),
			// Usually identical on every row (one integration, one file), so it yields width to the reason.
			flex: 0.95,
			minWidth: 185,
			sortable: false,
			renderCell: (params) => {
				const { source, fileS3Key, syncRunId } = params.row.latestStep;
				return (
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", minWidth: 0 }}>
						<ChangeSourceBadge source={source} />
						{fileS3Key ? (
							// Support can DOWNLOAD the raw file (network-wide → support-gated endpoint); everyone
							// else gets the key as copyable text, so no one can pull another facility's data.
							<MetaChip
								label={fileBasename(fileS3Key)}
								icon={isSupport ? <FileDownloadOutlinedIcon /> : <InsertDriveFileOutlinedIcon />}
								tooltip={isSupport ? t("employeeChangelog.shiftLineage.file.downloadTooltip", { key: fileS3Key }) : fileS3Key}
								onClick={isSupport ? () => void openRawFile(syncRunId) : () => copyFileKey(fileS3Key)}
								ariaLabel={isSupport ? t("employeeChangelog.shiftLineage.file.downloadAria") : t("employeeChangelog.shiftLineage.file.copyAria")}
								dataTestId={`shift-lineage-file-${params.row.key}`}
								sx={{ minWidth: 0 }}
							/>
						) : (
							<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, color: INK.DISABLED }}>{t("employeeChangelog.shiftLineage.file.none")}</Typography>
						)}
					</Box>
				);
			},
		},
	];

	/** The row's expanded detail: the shift's whole sync timeline, clamped to the height the grid reserved. */
	const renderExpand = (row: Row) => (
		<ShiftSyncTimeline steps={row.steps} timezone={timezone} subjectKey={row.key} maxHeight={expandHeight(row) - EXPAND_ROW.PADDING} />
	);

	// Arrived from a shift's raw punches: answer THAT shift and nothing else. The date/employee filters
	// are deliberately hidden — neither applies to a shift-scoped read, and showing them would suggest the
	// timeline below is a filtered slice rather than the shift's complete history.
	if (focus.shiftId) {
		return (
			<Box sx={{ height: "100%", minWidth: 0 }} data-testid="shift-lineage-view">
				<FocusedShiftLineage shiftId={focus.shiftId} nurseId={focus.nurseId} employeeName={focusedEmployeeName} onClearFocus={clearFocus} />
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }} data-testid="shift-lineage-view">
			<Box sx={{ display: "flex", alignItems: "end", gap: 2, flexWrap: "wrap", backgroundColor: NEUTRAL[50], p: 2, borderRadius: 1 }}>
				<DateRangeSelector
					label={t("employeeChangelog.shiftLineage.filter.dateRange")}
					startDate={start}
					endDate={end}
					onStartDateChange={(d) => {
						setStart(d);
						track("nurse_shift_lineage_date_filter_changed", { department_id: chosenDepartment?.department_id, filter_type: "from_date" });
					}}
					onEndDateChange={(d) => {
						setEnd(d);
						track("nurse_shift_lineage_date_filter_changed", { department_id: chosenDepartment?.department_id, filter_type: "to_date" });
					}}
					removeBackground
					containerSX={{ gap: 1 }}
				/>
				<SystemAutoComplete
					value={pickedNurseOption}
					onChange={(_: SyntheticEvent, value: SystemAutoCompleteOption<number> | null) => {
						setNurseId(value?.value);
						track("nurse_shift_lineage_nurse_filter_changed", { department_id: chosenDepartment?.department_id, nurse_id: value?.value ?? "none" });
					}}
					options={nurseOptions}
					disabled={!roster}
					textFieldProps={{
						label: t("employeeChangelog.shiftLineage.filter.employee"),
						required: true,
						inputSx: { minWidth: { xs: 210, xxl: 300 } },
						reserveHeightForHelperText: false,
					}}
					dataTestId="shift-lineage-employee-select"
				/>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
				{nurseId === undefined && isRosterError ? (
					<EmptyState icon={<HistoryToggleOffOutlinedIcon />} title={t("profile.roster.unavailable")} dataTestId="shift-lineage-roster-error" />
				) : nurseId === undefined ? (
					<EmptyState
						icon={<HistoryToggleOffOutlinedIcon />}
						title={t("employeeChangelog.shiftLineage.empty.selectEmployee.title")}
						hint={t("employeeChangelog.shiftLineage.empty.selectEmployee.hint")}
						dataTestId="shift-lineage-select-employee"
					/>
				) : !isLoading && !isError && groups.length === 0 ? (
					// SystemDataGrid ignores its `emptyState` slot while row expansion is enabled, so the
					// "no changes" state is rendered here instead of relying on the grid's default overlay.
					<EmptyState icon={<HistoryToggleOffOutlinedIcon />} title={t("employeeChangelog.shiftLineage.empty.noChanges")} dataTestId="shift-lineage-empty" />
				) : (
					<SystemDataGrid<Row>
						rows={groups}
						columns={columns}
						getRowId={(row) => row.key}
						loading={isLoading}
						error={isError}
						searchableColumns={[
							// searchableColumns.field must be a real row key, so each entry folds the rendered
							// text of its column into one searchable string.
							{
								field: "startAt",
								getValue: (row) => Object.values(shiftSubject(row) ?? {}).join(" "),
							},
							{ field: "outcome", getValue: (row) => outcomeLabel(row) },
							{ field: "latestStep", getValue: (row) => `${t(STEP_LABEL_KEY[row.latestStep.status])} ${latestReason(row)}` },
							{ field: "key", getValue: (row) => `${row.latestStep.source} ${row.latestStep.fileS3Key ?? ""}` },
						]}
						searchPlaceholder={t("employeeChangelog.shiftLineage.searchPlaceholder")}
						leadingToolbarNodes={<StatStrip items={summaryItems} dataTestId="shift-lineage-summary" />}
						getRowExpandContent={renderExpand}
						getExpandContentHeight={expandHeight}
						expandedContentSx={{ py: 1.5 }}
						dataTestId="shift-lineage-grid"
					/>
				)}
			</Box>
		</Box>
	);
};
